import React, { useRef, useState } from "react";
import { Plus, Upload, X, ImageIcon } from "lucide-react";
import { imageUrl, uploadImageFiles } from "../lib/api";
import { toast } from "sonner";

export default function ImageUploader({
  images = [],
  onChange,
  max = 5,
  compact = false,
  row = false,
  label = "Product images",
  hint = "Drag & drop or click to upload (max 5 images, 1 MB each · auto-resized to 500px)",
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const remaining = max - images.length;

  const uploadFiles = async (fileList) => {
    if (!fileList?.length || remaining <= 0) {
      if (remaining <= 0) toast.error(`Maximum ${max} images allowed`);
      return;
    }
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/")).slice(0, remaining);
    if (!files.length) {
      toast.error("Please choose image files only");
      return;
    }

    setUploading(true);
    try {
      const urls = await uploadImageFiles(files, remaining);
      onChange([...images, ...urls]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const tileClass = row ? "w-14 h-14" : compact ? "w-10 h-10" : "w-20 h-20";

  if (row) {
    return (
      <div data-testid="image-uploader-row">
        <label className="text-sm font-medium">{label}</label>
        <p className="text-xs mt-0.5 mb-2" style={{ color: "var(--text-muted)" }}>{hint}</p>
        <div className="flex flex-wrap gap-2 items-center">
          {images.map((src, i) => (
            <div key={src + i} className={`relative ${tileClass} rounded-md border overflow-hidden shrink-0`} style={{ borderColor: "var(--border-soft)" }}>
              <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 text-gray-700 grid place-items-center shadow-sm border"
                style={{ borderColor: "var(--border-soft)" }}
              >
                <X size={10} />
              </button>
            </div>
          ))}
          {remaining > 0 && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => !uploading && inputRef.current?.click()}
              className={`${tileClass} rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-0.5 shrink-0 cursor-pointer ${dragging ? "bg-[var(--drag-highlight)]" : ""}`}
              style={{ borderColor: dragging ? "var(--primary)" : "var(--primary)", color: "var(--primary)" }}
            >
              <Plus size={16} />
              <span className="text-[10px] font-medium">{uploading ? "..." : "Add"}</span>
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="mt-1.5" data-testid="image-uploader-compact">
        <div className="flex flex-wrap gap-1 mb-1">
          {images.map((src, i) => (
            <div key={src + i} className="relative w-10 h-10 rounded border overflow-hidden" style={{ borderColor: "var(--border-soft)" }}>
              <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-0 right-0 w-4 h-4 bg-black/60 text-white grid place-items-center"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
        {remaining > 0 && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer border border-dashed rounded px-2 py-1.5 text-[10px] text-center ${dragging ? "bg-[var(--drag-highlight)]" : ""}`}
            style={{ borderColor: dragging ? "var(--primary)" : "var(--border-soft)", color: "var(--text-muted)" }}
          >
            {uploading ? "Uploading..." : `+ Images (${images.length}/${max})`}
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
      </div>
    );
  }

  return (
    <div className="md:col-span-2" data-testid="image-uploader">
      <label className="text-sm font-medium">{label}</label>
      <p className="text-xs mt-0.5 mb-2" style={{ color: "var(--text-muted)" }}>{hint}</p>

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {images.map((src, i) => (
            <div key={src + i} className="relative w-20 h-20 rounded-lg border overflow-hidden group" style={{ borderColor: "var(--border-soft)" }}>
              <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 grid place-items-center"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragging ? "border-[var(--primary)] bg-[var(--drag-highlight)]" : ""}`}
          style={{ borderColor: dragging ? "var(--primary)" : "var(--border-soft)" }}
        >
          <div className="inline-flex flex-col items-center gap-2" style={{ color: "var(--text-muted)" }}>
            {uploading ? (
              <span className="text-sm">Uploading...</span>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full grid place-items-center" style={{ background: "var(--surface-tint)", color: "var(--primary)" }}>
                  {dragging ? <Upload size={22} /> : <ImageIcon size={22} />}
                </div>
                <span className="text-sm font-medium">Drag & drop images here</span>
                <span className="text-xs">or click to browse · {images.length}/{max} uploaded</span>
              </>
            )}
          </div>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
    </div>
  );
}
